import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
  TableHead,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ACCOUNTING_API, EINVOICE_API, handleApiError } from '../../../config/api';
import { accountingApi, einvoiceApi } from '../../../config/apiClient';
import { getCompanyInfo } from '../../../services/migration/companyDataService';
import { refreshB2BMirrorByInvoice } from '../../../services/ecommerceApi';
import GuideButton from '../../common/guide/GuideButton';

interface EInvoiceSendModalProps {
  open: boolean;
  onClose: () => void;
  invoiceId: string | null;
  companyId: string | null;
  onSuccess: () => void;
  // ✅ İade faturası: kaydetme sonrası otomatik gönderimde orijinal fatura referansını ön-seçmek için
  presetReturnRef?: { id: string; number: string; date: string } | null;
}

enum EInvoiceProfileType {
  TEMEL = 'TEMELFATURA',
  TICARI = 'TICARIFATURA',
}

enum EInvoiceTypeCode {
  SATIS = 'SATIS',
  IADE = 'IADE',
  TEVKIFAT = 'TEVKIFAT',
  ISTISNA = 'ISTISNA',
  OZELMATRAH = 'OZELMATRAH',
  IHRACAT = 'IHRACAT',
}

enum EInvoiceScenario {
  TEMEL = 'TEMELFATURA',
  TICARI = 'TICARIFATURA',
}

const EInvoiceSendModal: React.FC<EInvoiceSendModalProps> = ({
  open,
  onClose,
  invoiceId,
  companyId,
  onSuccess,
  presetReturnRef = null,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState<any>(null);
  
  // E-Fatura Detayları
  const [eInvoiceProfileType, setEInvoiceProfileType] = useState<EInvoiceProfileType>(EInvoiceProfileType.TEMEL);
  const [eInvoiceTypeCode, setEInvoiceTypeCode] = useState<EInvoiceTypeCode>(EInvoiceTypeCode.SATIS);
  const [eInvoiceScenario, setEInvoiceScenario] = useState<EInvoiceScenario>(EInvoiceScenario.TEMEL);
  
  // XML Önizleme
  const [xmlContent, setXmlContent] = useState<string>('');
  
  // Cari Bilgileri Kontrolü
  const [missingAccountFields, setMissingAccountFields] = useState<string[]>([]);

  // ✅ Şirket Bilgileri (Firebase'den)
  const [companyInfo, setCompanyInfo] = useState<any>(null);
  const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(false);

  // ✅ E-Fatura Entegrasyon Bilgileri (SQL'den)
  const [eInvoiceIntegration, setEInvoiceIntegration] = useState<any>(null);
  const [loadingIntegration, setLoadingIntegration] = useState(false);

  // ✅ EDM: hesapta kayıtlı fatura serileri + bu gönderime özel seri seçimi
  // '' = Otomatik (entegrasyon ayarındaki seri; o da boşsa EDM'den otomatik çözülür)
  const [edmSerials, setEdmSerials] = useState<any[]>([]);
  const [selectedSerial, setSelectedSerial] = useState<string>('');

  // ✅ Tasarım şablonu (XSLT) seçimi:
  // '' = Otomatik (varsayılan şablon; yoksa entegratör tasarımı),
  // 'none' = Entegratör tasarımı (şablon gömme), 'system' = Sistem varsayılanı, sayı = özel şablon Id.
  const [xsltTemplates, setXsltTemplates] = useState<any[]>([]);
  const [selectedXsltTemplate, setSelectedXsltTemplate] = useState<string>('');
  // Modal açılışında varsayılan şablon bir kez dropdown'a seçili yazılır;
  // sonrasında kullanıcının seçimine (Otomatik dahil) dokunulmaz.
  const xsltPreselectDoneRef = useRef(false);

  // ✅ Önizleme adımında faturanın görsel hali (seçili XSLT şablonuyla render edilir).
  // Akordeon açıldığında bir kez yüklenir; şablon yoksa bilgi mesajı gösterilir.
  const [visualPreviewHtml, setVisualPreviewHtml] = useState<string>('');
  const [visualPreviewError, setVisualPreviewError] = useState<string | null>(null);
  const [visualPreviewLoading, setVisualPreviewLoading] = useState(false);
  const [visualPreviewLoadedFor, setVisualPreviewLoadedFor] = useState<string>('');

  // E-Fatura Gönderim Sonuçları
  const [sendResult, setSendResult] = useState<any>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  // Çift tıklama / yarış koruması: state güncellemesi asenkron olduğu için ref ile anında kilit
  const sendingLockRef = useRef(false);

  // ✅ İade (IADE) faturası: iade edilen orijinal faturanın seçimi
  // GİB Schematron, IADE tipinde orijinal faturaya cac:BillingReference ister.
  const [returnCandidates, setReturnCandidates] = useState<any[]>([]);
  const [loadingReturnCandidates, setLoadingReturnCandidates] = useState(false);
  const [selectedReturnInvoiceId, setSelectedReturnInvoiceId] = useState<string>('');
  // Aday listesinde bulunamayan referans için yedek (localStorage köprüsü / preset)
  const [storedReturnRef, setStoredReturnRef] = useState<{ id: string; number?: string; date?: string } | null>(null);
  // İade referansı oluşturmada belirlendiyse modalda DEĞİŞTİRİLEMEZ (miktar/stok tutarlılığı bozulmasın).
  const [returnRefLocked, setReturnRefLocked] = useState<boolean>(false);

  // IADE/TEVKIFATIADE/YTBIADE → orijinal fatura referansı zorunlu
  const isReturnType = ['IADE', 'TEVKIFATIADE', 'YTBIADE'].includes(String(eInvoiceTypeCode).toUpperCase());
  const selectedReturnInvoice = returnCandidates.find((c) => c.id === selectedReturnInvoiceId) || null;
  const returnRefPayload = selectedReturnInvoice
    ? {
        returnedInvoiceId: selectedReturnInvoice.id,
        returnedInvoiceNumber: selectedReturnInvoice.invoiceNumber,
        returnedInvoiceDate: selectedReturnInvoice.invoiceDate,
      }
    : (storedReturnRef && storedReturnRef.id === selectedReturnInvoiceId
        ? {
            returnedInvoiceId: storedReturnRef.id,
            returnedInvoiceNumber: storedReturnRef.number,
            returnedInvoiceDate: storedReturnRef.date,
          }
        : {});

  const steps = ['Bilgi Kontrolü', 'Önizleme', 'Gönderim'];

  // Para biçimlendirme (önizleme için)
  const fmtMoney = (v: any, currency?: string) => {
    const n = Number(v) || 0;
    return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + (currency || invoiceData?.currency || 'TRY');
  };

  // Fatura verilerini yükle
  // Not: Entegrasyon, faturanın şubesine göre çözüleceği için loadInvoiceData içinden
  // (branchId belli olduktan sonra) yüklenir.
  useEffect(() => {
    if (open && invoiceId && companyId) {
      setSelectedSerial('');
      setEdmSerials([]);
      setSelectedXsltTemplate('');
      setXsltTemplates([]);
      xsltPreselectDoneRef.current = false;
      setVisualPreviewHtml('');
      setVisualPreviewError(null);
      setVisualPreviewLoadedFor('');
      loadInvoiceData();
      loadCompanyInfo();
    }
  }, [open, invoiceId, companyId]);

  // ✅ Entegrasyon ayarında kayıtlı seri, gönderim modalında otomatik seçili gelsin.
  // Belge tipine göre uygun önek: profil EARSIV ise e-arşiv serisi, değilse e-fatura serisi.
  // Kullanıcı elle seçim yaptıysa (selectedSerial dolu) dokunulmaz; modal her açılışta sıfırlanır.
  useEffect(() => {
    if (!open || eInvoiceIntegration?.providerType !== 'edm' || selectedSerial) return;
    const isArchiveDoc = String(invoiceData?.eInvoiceProfileType || '').toUpperCase().includes('ARSIV');
    const saved = isArchiveDoc
      ? (eInvoiceIntegration.eArsivSerialPrefix || eInvoiceIntegration.eInvoiceSerialPrefix)
      : eInvoiceIntegration.eInvoiceSerialPrefix;
    if (saved) setSelectedSerial(String(saved).trim().toUpperCase());
  }, [open, eInvoiceIntegration, invoiceData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ Şirket bilgilerini Firebase'den yükle
  const loadCompanyInfo = async () => {
    try {
      setLoadingCompanyInfo(true);
      const info = await getCompanyInfo(companyId);
      if (info) {
        setCompanyInfo(info);
        console.log('✅ [E-FATURA] Şirket bilgileri yüklendi:', {
          companyName: info?.companyName,
          taxNumber: info?.taxNumber,
          taxOffice: info?.taxOffice,
        });
      } else {
        console.warn('⚠️ [E-FATURA] Şirket bilgileri bulunamadı');
        setCompanyInfo({ name: 'Firma Bilgileri Yok' });
      }
    } catch (error) {
      console.error('❌ [E-FATURA] Şirket bilgileri yüklenirken hata:', error);
      setCompanyInfo({ name: 'Hata oluştu' });
    } finally {
      setLoadingCompanyInfo(false);
    }
  };

  // ✅ E-Fatura Entegrasyon bilgilerini SQL'den yükle.
  // Çözüm sırası (backend credential resolver ile aynı mantık):
  // 1) Faturanın şubesine özel EInvoiceIntegrations kaydı (franchise senaryosu)
  // 2) Ana şirket EInvoiceIntegrations kaydı
  // 3) PavoPayIntegrations kaydı (şube → genel) — PavoPay sekmesi ayrı tabloya yazar
  const loadEInvoiceIntegration = async (invoiceBranchId?: string | null) => {
    try {
      setLoadingIntegration(true);
      console.log('📥 [E-FATURA] Entegrasyon bilgileri yükleniyor...', { companyId, invoiceBranchId });

      const tryGetIntegration = async (branchId?: string | null): Promise<any | null> => {
        try {
          const url = branchId
            ? `${ACCOUNTING_API.EINVOICE_INTEGRATION_GET}?branchId=${encodeURIComponent(branchId)}`
            : ACCOUNTING_API.EINVOICE_INTEGRATION_GET;
          const response = await accountingApi.get(url);
          return response.data?.data || null;
        } catch {
          return null; // 404 = bu kapsamda kayıt yok
        }
      };

      // 1) Şube kapsamı → 2) ana şirket
      let integration = invoiceBranchId ? await tryGetIntegration(invoiceBranchId) : null;
      if (!integration) integration = await tryGetIntegration(null);

      // 3) PavoPay entegrasyonları (ayrı tablo) — şube eşleşmesi öncelikli
      if (!integration) {
        try {
          const ppResponse = await accountingApi.get(ACCOUNTING_API.PAVOPAY_INTEGRATION_LIST);
          const ppList: any[] = ppResponse.data?.data || [];
          const active = ppList.filter((p) => p.isActive !== false);
          const match =
            (invoiceBranchId && active.find((p) => p.branchId === invoiceBranchId)) ||
            active.find((p) => !p.branchId);
          if (match) {
            integration = {
              providerType: 'pavopay',
              username: match.username,
              taxNumber: '',
              companyName: match.branchName || '',
              branchId: match.branchId || null,
            };
          }
        } catch { /* PavoPay kaydı yok */ }
      }

      if (integration) {
        setEInvoiceIntegration(integration);
        console.log('✅ [E-FATURA] Aktif entegrasyon yüklendi:', integration);
        // EDM ise hesaptaki serileri arka planda getir (gönderimde seri seçimi için)
        loadEdmSerials(integration, invoiceBranchId);
        // Tasarım şablonlarını arka planda getir (gönderimde şablon seçimi için)
        loadXsltTemplates(invoiceBranchId);
      } else {
        console.warn('⚠️ [E-FATURA] Aktif entegrasyon yok');
        setSendError('Aktif e-fatura entegrasyonu bulunamadı. Lütfen önce entegrasyon ayarlarını yapın.');
      }
    } catch (error) {
      console.error('❌ [E-FATURA] Entegrasyon bilgileri yüklenirken hata:', error);
      setSendError('Entegrasyon bilgileri yüklenirken hata oluştu.');
    } finally {
      setLoadingIntegration(false);
    }
  };

  // ✅ EDM hesabında kayıtlı fatura serilerini getir (gönderimde seçim için).
  // Kritik değildir; hata durumunda dropdown yalnızca "Otomatik" seçeneğiyle kalır.
  const loadEdmSerials = async (integration: any, invoiceBranchId?: string | null) => {
    if (integration?.providerType !== 'edm') {
      setEdmSerials([]);
      return;
    }
    try {
      const resp = await einvoiceApi.post(EINVOICE_API.EDM_SERIALS, {
        branchId: integration.branchId || invoiceBranchId || null,
      });
      if (resp.data?.success) {
        setEdmSerials(resp.data.serials || []);
      }
    } catch (err) {
      console.warn('⚠️ [E-FATURA] Seri listesi alınamadı (Otomatik ile devam edilir):', err);
      setEdmSerials([]);
    }
  };

  // ✅ Şirketin tasarım şablonlarını (XSLT) getir (gönderimde seçim için).
  // Kritik değildir; hata durumunda dropdown sabit seçeneklerle (Otomatik/Entegratör/Sistem) kalır.
  const loadXsltTemplates = async (invoiceBranchId?: string | null) => {
    try {
      const resp = await einvoiceApi.post(EINVOICE_API.XSLT_TEMPLATES_LIST, {
        branchId: invoiceBranchId || null,
      });
      if (resp.data?.success) {
        setXsltTemplates(resp.data.items || []);
      }
    } catch (err) {
      console.warn('⚠️ [E-FATURA] Şablon listesi alınamadı (Otomatik ile devam edilir):', err);
      setXsltTemplates([]);
    }
  };

  // ✅ Varsayılan tasarım şablonu dropdown'da seçili görünsün.
  // Backend'in otomatik çözümüyle birebir aynı kural uygulanır (sonuç değişmez):
  // varsayılan + belge türü uyumlu şablonlar içinden önce faturanın şubesine özel
  // (türe özel olan HEPSI'yi ezer), yoksa şirket geneli seçilir.
  useEffect(() => {
    if (!open || xsltPreselectDoneRef.current || xsltTemplates.length === 0 || !invoiceData) return;
    xsltPreselectDoneRef.current = true;
    if (selectedXsltTemplate) return; // kullanıcı bu arada seçim yaptıysa dokunma

    const isArchiveDoc = String(invoiceData?.eInvoiceProfileType || '').toUpperCase().includes('ARSIV');
    const docType = isArchiveDoc ? 'EARSIV' : 'EFATURA';
    const invBranchId = invoiceData?.branchId || null;
    const typeFirst = (a: any, b: any) =>
      (a.documentType === 'HEPSI' ? 1 : 0) - (b.documentType === 'HEPSI' ? 1 : 0);
    const candidates = xsltTemplates.filter(
      (t: any) => t.isDefault && (t.documentType === docType || t.documentType === 'HEPSI')
    );
    const pick =
      candidates.filter((t: any) => invBranchId && t.branchId === invBranchId).sort(typeFirst)[0] ||
      candidates.filter((t: any) => !t.branchId).sort(typeFirst)[0];
    if (pick) setSelectedXsltTemplate(String(pick.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, xsltTemplates, invoiceData]);

  // ✅ Faturanın görsel önizlemesi: seçili (veya varsayılan) XSLT şablonuyla HTML render.
  // Aynı şablon seçimi için tekrar yüklenmez; şablon değiştiyse yeniden yüklenir.
  const loadVisualPreview = async () => {
    const key = `${selectedXsltTemplate || 'auto'}|${selectedSerial || 'auto'}`;
    if (visualPreviewLoading || visualPreviewLoadedFor === key) return;
    setVisualPreviewLoading(true);
    setVisualPreviewError(null);
    setVisualPreviewHtml('');
    try {
      const resp = await einvoiceApi.post(EINVOICE_API.XSLT_TEMPLATES_PREVIEW, {
        invoiceId,
        templateId: selectedXsltTemplate || '',
        ...(selectedSerial ? { serialPrefix: selectedSerial } : {}),
      });
      if (resp.data?.success) {
        setVisualPreviewHtml(resp.data.html || '');
      } else {
        setVisualPreviewError(resp.data?.error || 'Görsel önizleme oluşturulamadı.');
      }
      setVisualPreviewLoadedFor(key);
    } catch (err) {
      console.warn('⚠️ [E-FATURA] Görsel önizleme alınamadı:', err);
      setVisualPreviewError(handleApiError(err));
      setVisualPreviewLoadedFor(key);
    } finally {
      setVisualPreviewLoading(false);
    }
  };

  // ✅ İade faturası için: aynı carinin diğer faturalarını getir (orijinal faturayı seçtirmek üzere)
  const loadReturnCandidates = async (accountId: string, accountName?: string): Promise<any[]> => {
    if (!accountId) return [];
    try {
      setLoadingReturnCandidates(true);
      // Cari adıyla arama yapıp client-side accountId'ye göre filtreliyoruz (geniş eşleşmeleri eler).
      const url = ACCOUNTING_API.INVOICE_BY_COMPANY({ page: 1, pageSize: 200, search: accountName || '' });
      const resp = await accountingApi.get(url);
      const list: any[] = resp.data?.data?.items || resp.data?.items || resp.data?.data || [];
      const candidates = (Array.isArray(list) ? list : [])
        .filter((inv) => String(inv.accountId) === String(accountId))
        .filter((inv) => inv.id !== invoiceId)
        .sort((a, b) => new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime());
      setReturnCandidates(candidates);
      console.log('✅ [E-FATURA][IADE] Aday orijinal faturalar:', candidates.length);
      return candidates;
    } catch (err) {
      console.error('❌ [E-FATURA][IADE] Aday faturalar yüklenemedi:', err);
      setReturnCandidates([]);
      return [];
    } finally {
      setLoadingReturnCandidates(false);
    }
  };

  const loadInvoiceData = async () => {
    setLoading(true);
    try {
      const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(invoiceId!));
      const result = response.data;
      const data = result.data;
      
      setInvoiceData(data);

      // ✅ Entegrasyonu faturanın şubesine göre çöz (franchise → ana şirket → PavoPay)
      loadEInvoiceIntegration(data?.branchId || null);

      // ✅ Backend'den gelen değeri enum değerine map et
      const backendProfileType = (data.eInvoiceProfileType || 'TEMEL').toUpperCase();
      const profileType = backendProfileType.includes('TEMEL') 
        ? EInvoiceProfileType.TEMEL 
        : EInvoiceProfileType.TICARI;
      
      const backendTypeCode = (data.eInvoiceTypeCode || 'SATIS').toUpperCase();
      const typeCode = (EInvoiceTypeCode as any)[backendTypeCode] || EInvoiceTypeCode.SATIS;
      
      const backendScenario = (data.eInvoiceScenario || 'TEMEL').toUpperCase();
      const scenario = backendScenario.includes('TEMEL')
        ? EInvoiceScenario.TEMEL
        : EInvoiceScenario.TICARI;
      
      setEInvoiceProfileType(profileType);
      setEInvoiceTypeCode(typeCode);
      setEInvoiceScenario(scenario);
      
      console.log('✅ [E-FATURA] Profil türü yüklendi:', {
        backend: data.eInvoiceProfileType,
        mapped: profileType,
        isTemel: profileType === EInvoiceProfileType.TEMEL
      });
      
      // Eksik cari bilgilerini kontrol et
      checkMissingAccountFields(data);

      // ✅ İade faturası ise: orijinal fatura adaylarını yükle + DB'de referans varsa ön-seç
      const isReturn = ['IADE', 'TEVKIFATIADE', 'YTBIADE'].includes(String(typeCode).toUpperCase());
      if (isReturn && data?.accountId) {
        const candidates = await loadReturnCandidates(String(data.accountId), data.accountName);

        // İade edilen orijinal fatura referansını çöz (öncelik sırası):
        // 1) prop ile gelen preset, 2) DB'deki returnedInvoiceId (prod kalıcılaştırınca),
        // 3) "İade Faturası Oluştur" butonuyla kaydedilirken localStorage'a yazılan köprü.
        let ref: { id: string; number?: string; date?: string } | null = null;
        if (presetReturnRef?.id) {
          ref = { id: presetReturnRef.id, number: presetReturnRef.number, date: presetReturnRef.date };
        } else if (data.returnedInvoiceId) {
          ref = { id: String(data.returnedInvoiceId), number: data.returnedInvoiceNumber, date: data.returnedInvoiceDate };
        } else {
          try {
            const raw = localStorage.getItem(`gts_return_ref_${invoiceId}`);
            if (raw) ref = JSON.parse(raw);
          } catch { /* yok say */ }
        }

        if (ref?.id) {
          setStoredReturnRef(ref);
          // Oluşturmada/önceki adımda belirlenmiş referans → modalda kilitli (değiştirilemez)
          setReturnRefLocked(true);
          // Aday listesinde yoksa sentetik bir kayıt ekle ki Select gösterebilsin
          const exists = (candidates || []).some((c: any) => String(c.id) === String(ref!.id));
          if (!exists) {
            setReturnCandidates((prev) => [
              { id: ref!.id, invoiceNumber: ref!.number, invoiceDate: ref!.date },
              ...prev,
            ]);
          }
          setSelectedReturnInvoiceId(String(ref.id));
        }
      }
    } catch (error) {
      console.error('Fatura yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkMissingAccountFields = (data: any) => {
    const missing: string[] = [];
    if (!data.accountTaxNumber) missing.push('Vergi Numarası');
    // ⚠️ Vergi Dairesi ARTIK gönderimi bloke ETMEZ:
    //   E-arşivde (özellikle TCKN'li bireysel alıcıda) vergi dairesi zorunlu değildir ve
    //   backend UBL builder alıcı vergi dairesini opsiyonel işler (boşsa PartyTaxScheme eklenmez).
    //   Bilgi amaçlı ayrı bir alanda gösterilir (bkz. optionalMissingAccountFields).
    if (!data.accountAddress) missing.push('Adres');
    setMissingAccountFields(missing);
  };

  // Gönderimi engellemeyen, yalnızca öneri niteliğinde eksik alanlar (ör. Vergi Dairesi).
  const getOptionalMissingAccountFields = (): string[] => {
    const opt: string[] = [];
    if (invoiceData && !invoiceData.accountTaxOffice) opt.push('Vergi Dairesi');
    return opt;
  };

  const getMissingAccountFields = () => missingAccountFields;

  // ✅ Enum değerini kullanıcı dostu formata çevir
  const getDisplayProfileType = (profileType: EInvoiceProfileType): string => {
    return profileType === EInvoiceProfileType.TEMEL ? 'TEMEL FATURA' : 'TİCARİ FATURA';
  };

  // ✅ UUID Generator (RFC 4122)
  const generateUUID = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16).toUpperCase();
    });
  };

  // ✅ Fatura numarasını GİB formatına çevir (tire'leri kaldır)
  const formatInvoiceNumberForGIB = (invoiceNumber: string): string => {
    return invoiceNumber.replace(/-/g, '');
  };

  // ✅ UBL XML Oluşturma (GİB Standardı)
  const generateInvoiceXml = (): string => {
    if (!invoiceData) return '<!-- Fatura verisi yükleniyor... -->';

    // UUID oluştur (her seferinde yeni)
    const invoiceUUID = generateUUID();
    
    // Fatura numarasını GİB formatına çevir (tire'siz)
    const gibInvoiceNumber = formatInvoiceNumberForGIB(invoiceData.invoiceNumber);

    const formatDate = (date: string) => {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const formatTime = () => {
      const now = new Date();
      return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    };

    const formatAmount = (amount: number) => amount.toFixed(2);

    const lineCount = invoiceData.items?.length || 0;

    // ✅ Backend'den gelen doğru totals'ları kullan (yeniden hesaplama!)
    const totalVat = invoiceData.vatTotal || 0;
    const lineExtensionTotal = invoiceData.subtotal || 0;
    const allowanceTotal = invoiceData.discountTotal || 0;
    const taxExclusiveAmount = lineExtensionTotal - allowanceTotal;
    const taxInclusiveAmount = invoiceData.grandTotal || 0; // ✅ Backend'in hesapladığı tam tutar

    // Profil türüne göre XML şablonu
    if (eInvoiceProfileType === EInvoiceProfileType.TEMEL) {
      // ✅ GİB TEMEL FATURA STANDARDINA UYGUN XML (TemelFaturaOrnegi.xml)
      return `<?xml version="1.0" encoding="UTF-8"?>
<?xml-stylesheet type="text/xsl" href="general.xslt"?>
<Invoice xsi:schemaLocation="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2 ../xsdrt/maindoc/UBL-Invoice-2.1.xsd" xmlns="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2" xmlns:n4="http://www.altova.com/samplexml/other-namespace" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:cac="urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2" xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2" xmlns:ext="urn:oasis:names:specification:ubl:schema:xsd:CommonExtensionComponents-2">
	<ext:UBLExtensions>
		<ext:UBLExtension>
			<ext:ExtensionContent>
				<n4:auto-generated_for_wildcard/>
			</ext:ExtensionContent>
		</ext:UBLExtension>
	</ext:UBLExtensions>
	<cbc:UBLVersionID>2.1</cbc:UBLVersionID>
	<cbc:CustomizationID>TR1.2</cbc:CustomizationID>
	<cbc:ProfileID>TEMELFATURA</cbc:ProfileID>
	<cbc:ID>${gibInvoiceNumber}</cbc:ID>
	<cbc:CopyIndicator>false</cbc:CopyIndicator>
	<cbc:UUID>${invoiceUUID}</cbc:UUID>
	<cbc:IssueDate>${formatDate(invoiceData.invoiceDate)}</cbc:IssueDate>
	<cbc:IssueTime>${formatTime()}</cbc:IssueTime>
	<cbc:InvoiceTypeCode>${eInvoiceTypeCode}</cbc:InvoiceTypeCode>
	<cbc:DocumentCurrencyCode>${invoiceData.currency || 'TRY'}</cbc:DocumentCurrencyCode>
	<cbc:LineCountNumeric>${lineCount}</cbc:LineCountNumeric>
	<cac:InvoicePeriod>
		<cbc:StartDate>${formatDate(invoiceData.invoiceDate)}</cbc:StartDate>
		<cbc:EndDate>${formatDate(invoiceData.invoiceDate)}</cbc:EndDate>
	</cac:InvoicePeriod>
	<cac:Signature>
		<cbc:ID schemeID="VKN_TCKN">${companyInfo?.taxNumber || ''}</cbc:ID>
		<cac:SignatoryParty>
			<cac:PartyIdentification>
				<cbc:ID schemeID="VKN">${companyInfo?.taxNumber || ''}</cbc:ID>
			</cac:PartyIdentification>
			<cac:PostalAddress>
				<cbc:StreetName>${companyInfo?.address || 'Adres bilgisi eksik'}</cbc:StreetName>
				<cbc:BuildingNumber>1</cbc:BuildingNumber>
				<cbc:CitySubdivisionName>Merkez</cbc:CitySubdivisionName>
				<cbc:CityName>İstanbul</cbc:CityName>
				<cbc:PostalZone>34000</cbc:PostalZone>
				<cac:Country>
					<cbc:Name>Türkiye</cbc:Name>
				</cac:Country>
			</cac:PostalAddress>
		</cac:SignatoryParty>
		<cac:DigitalSignatureAttachment>
			<cac:ExternalReference>
				<cbc:URI>#Signature</cbc:URI>
			</cac:ExternalReference>
		</cac:DigitalSignatureAttachment>
	</cac:Signature>
 	<cac:AccountingSupplierParty>
		<cac:Party>
			<cbc:WebsiteURI>${companyInfo?.website || 'http://www.firma.com.tr'}</cbc:WebsiteURI>
			<cac:PartyIdentification>
				<cbc:ID schemeID="VKN">${companyInfo?.taxNumber || ''}</cbc:ID>
			</cac:PartyIdentification>
			<cac:PartyName>
				<cbc:Name>${companyInfo?.companyName || ''}</cbc:Name>
			</cac:PartyName>
			<cac:PostalAddress>
				<cbc:ID>1234567890</cbc:ID>
				<cbc:StreetName>${companyInfo?.address || 'Adres bilgisi eksik'}</cbc:StreetName>
				<cbc:BuildingNumber>1</cbc:BuildingNumber>
				<cbc:CitySubdivisionName>Merkez</cbc:CitySubdivisionName>
				<cbc:CityName>İstanbul</cbc:CityName>
				<cbc:PostalZone>34000</cbc:PostalZone>
				<cac:Country>
					<cbc:Name>Türkiye</cbc:Name>
				</cac:Country>
			</cac:PostalAddress>
			<cac:PartyTaxScheme>
				<cac:TaxScheme>
					<cbc:Name>${companyInfo?.taxOffice || 'Vergi Dairesi'}</cbc:Name>
				</cac:TaxScheme>
			</cac:PartyTaxScheme>
			<cac:Contact>
				<cbc:Telephone>${companyInfo?.phone || '0(000) 000 00 00'}</cbc:Telephone>
				<cbc:Telefax>${companyInfo?.phone || '0(000) 000 00 00'}</cbc:Telefax>
				<cbc:ElectronicMail>${companyInfo?.email || 'bilgi@firma.com'}</cbc:ElectronicMail>
			</cac:Contact>
		</cac:Party>
	</cac:AccountingSupplierParty>
	<cac:AccountingCustomerParty>
		<cac:Party>
			<cbc:WebsiteURI/>
			<cac:PartyIdentification>
				<cbc:ID schemeID="${invoiceData.accountTaxNumber?.length === 11 ? 'TCKN' : 'VKN'}">${invoiceData.accountTaxNumber || ''}</cbc:ID>
			</cac:PartyIdentification>
			${invoiceData.accountTaxNumber?.length !== 11 ? `<cac:PartyName>
				<cbc:Name>${invoiceData.accountName || 'Firma Ünvanı'}</cbc:Name>
			</cac:PartyName>` : ''}
			<cac:PostalAddress>
				<cbc:ID>${invoiceData.accountAddress?.substring(0, 50) || 'Mahalle/Semt'}</cbc:ID>
				<cbc:StreetName>${invoiceData.accountAddress || 'Adres bilgisi eksik'}</cbc:StreetName>
				<cbc:BuildingNumber>1</cbc:BuildingNumber>
				<cbc:CitySubdivisionName>Merkez</cbc:CitySubdivisionName>
				<cbc:CityName>Ankara</cbc:CityName>
				<cbc:PostalZone>06000</cbc:PostalZone>
				<cac:Country>
					<cbc:Name>Türkiye</cbc:Name>
				</cac:Country>
			</cac:PostalAddress>
			<cac:Contact>
				<cbc:ElectronicMail>${invoiceData.accountEmail || 'bilgi@musteri.com'}</cbc:ElectronicMail>
			</cac:Contact>
			${invoiceData.accountTaxNumber?.length === 11 ? `<cac:Person>
				<cbc:FirstName>${invoiceData.accountName?.split(' ')[0] || 'Ad'}</cbc:FirstName>
				<cbc:FamilyName>${invoiceData.accountName?.split(' ').slice(1).join(' ') || 'Soyad'}</cbc:FamilyName>
			</cac:Person>` : ''}
		</cac:Party>
	</cac:AccountingCustomerParty>
	<cac:PaymentTerms>
		<cbc:Note>${invoiceData.paymentNote || 'Fatura düzenlenme tarihinden itibaren 30 gün içerisinde ödenecektir.'}</cbc:Note>
		<cbc:PaymentDueDate>${invoiceData.dueDate ? formatDate(invoiceData.dueDate) : formatDate(new Date(new Date(invoiceData.invoiceDate).getTime() + 30*24*60*60*1000).toISOString())}</cbc:PaymentDueDate>
	</cac:PaymentTerms>
	<cac:TaxTotal>
		<cbc:TaxAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(totalVat)}</cbc:TaxAmount>
		<cac:TaxSubtotal>
			<cbc:TaxableAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(taxExclusiveAmount)}</cbc:TaxableAmount>
			<cbc:TaxAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(totalVat)}</cbc:TaxAmount>
			<cac:TaxCategory>
				<cac:TaxScheme>
					<cbc:TaxTypeCode>0015</cbc:TaxTypeCode>
				</cac:TaxScheme>
			</cac:TaxCategory>
		</cac:TaxSubtotal>
	</cac:TaxTotal>
	<cac:LegalMonetaryTotal>
		<cbc:LineExtensionAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(lineExtensionTotal)}</cbc:LineExtensionAmount>
		<cbc:TaxExclusiveAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(taxExclusiveAmount)}</cbc:TaxExclusiveAmount>
		<cbc:TaxInclusiveAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(taxInclusiveAmount)}</cbc:TaxInclusiveAmount>
		<cbc:PayableAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(taxInclusiveAmount)}</cbc:PayableAmount>
	</cac:LegalMonetaryTotal>
${invoiceData.items?.map((item: any, index: number) => {
  // ✅ Backend'den gelen doğru değerleri kullan (yeniden hesaplama!)
  const lineTotal = item.lineTotal || 0;
  const discountAmount = item.discountAmount || 0;
  const lineExtension = item.netTotal || 0;
  const vatAmount = item.vatAmount || 0;
  
  return `	<cac:InvoiceLine>
		<cbc:ID>${index + 1}</cbc:ID>
		<cbc:InvoicedQuantity unitCode="${item.unit === 'Adet' ? 'NIU' : item.unit === 'Kutu' ? 'BX' : 'C62'}">${item.quantity || 0}</cbc:InvoicedQuantity>
		<cbc:LineExtensionAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(lineExtension)}</cbc:LineExtensionAmount>
		<cac:AllowanceCharge>
			<cbc:ChargeIndicator>false</cbc:ChargeIndicator>
			<cbc:MultiplierFactorNumeric>${(item.discountRate || 0) / 100}</cbc:MultiplierFactorNumeric>
			<cbc:Amount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(discountAmount)}</cbc:Amount>
			<cbc:BaseAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(lineTotal)}</cbc:BaseAmount>
		</cac:AllowanceCharge>
		<cac:TaxTotal>
			<cbc:TaxAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(vatAmount)}</cbc:TaxAmount>
			<cac:TaxSubtotal>
				<cbc:TaxableAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(lineExtension)}</cbc:TaxableAmount>
				<cbc:TaxAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(vatAmount)}</cbc:TaxAmount>
				<cbc:Percent>${item.vatRate || 0}.0</cbc:Percent>
				<cac:TaxCategory>
					<cac:TaxScheme>
						<cbc:Name>KDV</cbc:Name>
						<cbc:TaxTypeCode>0015</cbc:TaxTypeCode>
					</cac:TaxScheme>
				</cac:TaxCategory>
			</cac:TaxSubtotal>
		</cac:TaxTotal>
		<cac:Item>
			<cbc:Name>${item.itemName || 'Ürün'}</cbc:Name>
		</cac:Item>
		<cac:Price>
			<cbc:PriceAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(item.unitPrice || 0)}</cbc:PriceAmount>
		</cac:Price>
	</cac:InvoiceLine>`;
}).join('\n')}
</Invoice>`;
    } else {
      // TİCARİ FATURA XML - GİB ÖRNEĞİYLE BİREBİR AYNI
      return `<?xml version="1.0" encoding="UTF-8"?>
<?xml-stylesheet type="text/xsl" href="general.xslt"?>
<Invoice xmlns="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:cac="urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2" xmlns:ext="urn:oasis:names:specification:ubl:schema:xsd:CommonExtensionComponents-2" xmlns:ds="http://www.w3.org/2000/09/xmldsig#" xmlns:xades="http://uri.etsi.org/01903/v1.3.2#" xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2" xsi:schemaLocation="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2 ../xsdrt/maindoc/UBL-Invoice-2.1.xsd" xmlns:n4="http://www.altova.com/samplexml/other-namespace" >
	<ext:UBLExtensions>
		<ext:UBLExtension>
			<ext:ExtensionContent>
				<n4:auto-generated_for_wildcard/>
			</ext:ExtensionContent>
		</ext:UBLExtension>
	</ext:UBLExtensions>
	<cbc:UBLVersionID>2.1</cbc:UBLVersionID>
	<cbc:CustomizationID>TR1.2</cbc:CustomizationID>
	<cbc:ProfileID>TICARIFATURA</cbc:ProfileID>
	<cbc:ID>${gibInvoiceNumber}</cbc:ID>
	<cbc:CopyIndicator>false</cbc:CopyIndicator>
	<cbc:UUID>${invoiceUUID}</cbc:UUID>
	<cbc:IssueDate>${formatDate(invoiceData.invoiceDate)}</cbc:IssueDate>
	<cbc:IssueTime>${formatTime()}</cbc:IssueTime>
	<cbc:InvoiceTypeCode>${eInvoiceTypeCode}</cbc:InvoiceTypeCode>
	<cbc:DocumentCurrencyCode>${invoiceData.currency || 'TRY'}</cbc:DocumentCurrencyCode>
	<cbc:LineCountNumeric>${lineCount}</cbc:LineCountNumeric>
	${invoiceData.despatchDocumentNumber ? `<cac:DespatchDocumentReference>
		<cbc:ID>${invoiceData.despatchDocumentNumber}</cbc:ID>
		${invoiceData.despatchDocumentDate ? `<cbc:IssueDate>${formatDate(invoiceData.despatchDocumentDate)}</cbc:IssueDate>` : ''}
	</cac:DespatchDocumentReference>` : ''}
	<cac:Signature>
		<cbc:ID schemeID="VKN_TCKN">${companyInfo?.taxNumber || ''}</cbc:ID>
		<cac:SignatoryParty>
			<cac:PartyIdentification>
				<cbc:ID schemeID="VKN">${companyInfo?.taxNumber || ''}</cbc:ID>
			</cac:PartyIdentification>
			<cac:PostalAddress>
				<cbc:StreetName>${companyInfo?.address || 'Adres bilgisi eksik'}</cbc:StreetName>
				<cbc:BuildingNumber>1</cbc:BuildingNumber>
				<cbc:CitySubdivisionName>Merkez</cbc:CitySubdivisionName>
				<cbc:CityName>İstanbul</cbc:CityName>
				<cbc:PostalZone>34000</cbc:PostalZone>
				<cac:Country>
					<cbc:Name>Türkiye</cbc:Name>
				</cac:Country>
			</cac:PostalAddress>
		</cac:SignatoryParty>
		<cac:DigitalSignatureAttachment>
			<cac:ExternalReference>
				<cbc:URI>#Signature</cbc:URI>
			</cac:ExternalReference>
		</cac:DigitalSignatureAttachment>
	</cac:Signature>
 	<cac:AccountingSupplierParty>
		<cac:Party>
			<cbc:WebsiteURI>${companyInfo?.website || 'http://www.firma.com.tr'}</cbc:WebsiteURI>
			<cac:PartyIdentification>
				<cbc:ID schemeID="VKN">${companyInfo?.taxNumber || ''}</cbc:ID>
			</cac:PartyIdentification>
			<cac:PartyName>
				<cbc:Name>${companyInfo?.companyName || ''}</cbc:Name>
			</cac:PartyName>
			<cac:PostalAddress>
				<cbc:ID>1234567890</cbc:ID>
				<cbc:StreetName>${companyInfo?.address || 'Adres bilgisi eksik'}</cbc:StreetName>
				<cbc:BuildingNumber>1</cbc:BuildingNumber>
				<cbc:CitySubdivisionName>Merkez</cbc:CitySubdivisionName>
				<cbc:CityName>İstanbul</cbc:CityName>
				<cbc:PostalZone>34000</cbc:PostalZone>
				<cac:Country>
					<cbc:Name>Türkiye</cbc:Name>
				</cac:Country>
			</cac:PostalAddress>
			<cac:PartyTaxScheme>
				<cac:TaxScheme>
					<cbc:Name>${companyInfo?.taxOffice || 'Vergi Dairesi'}</cbc:Name>
				</cac:TaxScheme>
			</cac:PartyTaxScheme>
			<cac:Contact>
				<cbc:Telephone>${companyInfo?.phone || '0(000) 000 00 00'}</cbc:Telephone>
				<cbc:Telefax>${companyInfo?.phone || '0(000) 000 00 00'}</cbc:Telefax>
				<cbc:ElectronicMail>${companyInfo?.email || 'bilgi@firma.com'}</cbc:ElectronicMail>
			</cac:Contact>
		</cac:Party>
	</cac:AccountingSupplierParty>
	<cac:AccountingCustomerParty>
		<cac:Party>
			<cbc:WebsiteURI>http://www.musteri.com.tr/</cbc:WebsiteURI>
			<cac:PartyIdentification>
				<cbc:ID schemeID="VKN">${invoiceData.accountTaxNumber || ''}</cbc:ID>
			</cac:PartyIdentification>
			<cac:PartyName>
				<cbc:Name>${invoiceData.accountName || ''}</cbc:Name>
			</cac:PartyName>
			<cac:PostalAddress>
				<cbc:ID>1234567890</cbc:ID>
				<cbc:StreetName>${invoiceData.accountAddress || 'Adres bilgisi eksik'}</cbc:StreetName>
				<cbc:BuildingNumber>1</cbc:BuildingNumber>
				<cbc:CitySubdivisionName>Merkez</cbc:CitySubdivisionName>
				<cbc:CityName>Ankara</cbc:CityName>
				<cbc:PostalZone>06000</cbc:PostalZone>
				<cac:Country>
					<cbc:Name>Türkiye</cbc:Name>
				</cac:Country>
			</cac:PostalAddress>
			<cac:PartyTaxScheme>
				<cac:TaxScheme>
					<cbc:Name>${invoiceData.accountTaxOffice || 'Vergi Dairesi'}</cbc:Name>
				</cac:TaxScheme>
			</cac:PartyTaxScheme>
			<cac:Contact>
				<cbc:Telephone>${invoiceData.accountPhone || '0(000) 000 00 00'}</cbc:Telephone>
				<cbc:Telefax>${invoiceData.accountPhone || '0(000) 000 00 00'}</cbc:Telefax>
				<cbc:ElectronicMail>${invoiceData.accountEmail || 'bilgi@musteri.com'}</cbc:ElectronicMail>
			</cac:Contact>
		</cac:Party>
	</cac:AccountingCustomerParty>
	<cac:PaymentMeans>
		<cbc:PaymentMeansCode>1</cbc:PaymentMeansCode>
		<cac:PayeeFinancialAccount>
			<cbc:ID>${invoiceData.iban || '0000000000000000000000'}</cbc:ID>
			<cbc:CurrencyCode>${invoiceData.currency || 'TRY'}</cbc:CurrencyCode>
			<cbc:PaymentNote>${invoiceData.paymentNote || 'Banka Hesap Bilgisi'}</cbc:PaymentNote>
		</cac:PayeeFinancialAccount>
	</cac:PaymentMeans>
	<cac:PaymentTerms>
		<cbc:Note>${invoiceData.paymentNote || 'Fatura düzenlenme tarihinden itibaren 30 gün içerisinde ödenecektir.'}</cbc:Note>
		<cbc:PaymentDueDate>${invoiceData.dueDate ? formatDate(invoiceData.dueDate) : formatDate(new Date(new Date(invoiceData.invoiceDate).getTime() + 30*24*60*60*1000).toISOString())}</cbc:PaymentDueDate>
	</cac:PaymentTerms>
	<cac:TaxTotal>
		<cbc:TaxAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(totalVat)}</cbc:TaxAmount>
		<cac:TaxSubtotal>
			<cbc:TaxableAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(taxExclusiveAmount)}</cbc:TaxableAmount>
			<cbc:TaxAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(totalVat)}</cbc:TaxAmount>
			<cbc:CalculationSequenceNumeric>1.0</cbc:CalculationSequenceNumeric>
			<cbc:Percent>18</cbc:Percent>
			<cac:TaxCategory>
				<cac:TaxScheme>
					<cbc:Name>Katma Değer Vergisi</cbc:Name>
					<cbc:TaxTypeCode>0015</cbc:TaxTypeCode>
				</cac:TaxScheme>
			</cac:TaxCategory>
		</cac:TaxSubtotal>
	</cac:TaxTotal>
	<cac:LegalMonetaryTotal>
		<cbc:LineExtensionAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(lineExtensionTotal)}</cbc:LineExtensionAmount>
		<cbc:TaxExclusiveAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(taxExclusiveAmount)}</cbc:TaxExclusiveAmount>
		<cbc:TaxInclusiveAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(taxInclusiveAmount)}</cbc:TaxInclusiveAmount>
		<cbc:AllowanceTotalAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(allowanceTotal > 0 ? allowanceTotal : 0)} </cbc:AllowanceTotalAmount>
		<cbc:PayableAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(taxInclusiveAmount)}</cbc:PayableAmount>
	</cac:LegalMonetaryTotal>
${invoiceData.items?.map((item: any, index: number) => {
  // ✅ Backend'den gelen doğru değerleri kullan (yeniden hesaplama!)
  const lineTotal = item.lineTotal || 0;
  const discountAmount = item.discountAmount || 0;
  const lineExtension = item.netTotal || 0;
  const vatAmount = item.vatAmount || 0;
  
  return `    <cac:InvoiceLine>
        <cbc:ID>${index + 1}</cbc:ID>
        <cbc:InvoicedQuantity unitCode="${item.unit === 'Adet' ? 'NIU' : item.unit === 'Kutu' ? 'BX' : 'C62'}">${item.quantity || 0}</cbc:InvoicedQuantity>
        <cbc:LineExtensionAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(lineExtension)}</cbc:LineExtensionAmount>
        <cac:AllowanceCharge>
            <cbc:ChargeIndicator>false</cbc:ChargeIndicator>
            <cbc:MultiplierFactorNumeric>${(item.discountRate || 0) / 100}</cbc:MultiplierFactorNumeric>
            <cbc:Amount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(discountAmount)}</cbc:Amount>
            <cbc:BaseAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(lineTotal)}</cbc:BaseAmount>
        </cac:AllowanceCharge>
        <cac:TaxTotal>
            <cbc:TaxAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(vatAmount)}</cbc:TaxAmount>
            <cac:TaxSubtotal>
                <cbc:TaxableAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(lineExtension)}</cbc:TaxableAmount>
                <cbc:TaxAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(vatAmount)}</cbc:TaxAmount>
                <cbc:Percent>${item.vatRate || 0}</cbc:Percent>
                <cac:TaxCategory>
                    <cac:TaxScheme>
                        <cbc:Name>KDV</cbc:Name>
                        <cbc:TaxTypeCode>0015</cbc:TaxTypeCode>
                    </cac:TaxScheme>
                </cac:TaxCategory>
            </cac:TaxSubtotal>
        </cac:TaxTotal>
        <cac:Item>
            <cbc:Name>${item.itemName || 'Ürün'}</cbc:Name>
        </cac:Item>
        <cac:Price>
            <cbc:PriceAmount currencyID="${invoiceData.currency || 'TRY'}">${formatAmount(item.unitPrice || 0)}</cbc:PriceAmount>
        </cac:Price>
    </cac:InvoiceLine>`;
}).join('\n')}
</Invoice>`;
    }
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      // ✅ İade faturasında orijinal fatura seçimi zorunlu (GİB BillingReference şartı)
      if (isReturnType && !selectedReturnInvoiceId) {
        setSendError('İade faturası için iade edilen orijinal faturayı seçmelisiniz.');
        return;
      }
      setSendError(null);
      // EDM/PavoPay: UBL'yi backend üretir (ubl-preview). Finansbank: eski client-side üretim.
      if (['edm', 'pavopay'].includes(eInvoiceIntegration?.providerType)) {
        try {
          const resp = await einvoiceApi.post(EINVOICE_API.EDM_UBL_PREVIEW, {
            invoiceId,
            ...returnRefPayload,
            ...(selectedSerial ? { serialPrefix: selectedSerial } : {}),
          });
          const xml = typeof resp.data === 'string' ? resp.data : JSON.stringify(resp.data, null, 2);
          setXmlContent(xml);
          console.log('✅ [E-FATURA] EDM UBL önizleme alındı, uzunluk:', xml.length);
        } catch (err) {
          console.error('❌ [E-FATURA] UBL önizleme hatası:', err);
          setXmlContent(`<!-- UBL önizleme alınamadı: ${handleApiError(err)} -->`);
        }
      } else {
        console.log('🔨 [E-FATURA] XML oluşturuluyor (Finansbank)...');
        const xml = generateInvoiceXml();
        setXmlContent(xml);
        console.log('✅ [E-FATURA] XML oluşturuldu, uzunluk:', xml.length);
      }
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  // E-Fatura durumunu SQL'e kaydet
  const updateInvoiceEInvoiceStatus = async (sendResult: any) => {
    try {
      console.log('💾 [E-FATURA] SQL güncelleme başlıyor...', sendResult);

      const updatePayload = {
        companyId: companyId,
        eInvoiceStatus: 'SENT', // Gönderildi
        eInvoiceUUID: invoiceData?.eInvoiceUUID || null, // XML'de oluşturduğumuz UUID
        eInvoiceETTN: sendResult.belgeDurum?.data?.ettn || null, // GİB'den dönen ETTN (varsa)
        eInvoiceProvider: eInvoiceIntegration?.providerType || 'finansbank',
        belgeOid: sendResult.belgeGonderMsgOid || null, // QNB Belge OID
        eInvoiceSentDate: new Date().toISOString(),
        eInvoiceErrorMessage: null,
      };

      console.log('📤 [E-FATURA] SQL güncelleme payload:', updatePayload);

      const response = await accountingApi.post(ACCOUNTING_API.INVOICE_UPDATE_EINVOICE_STATUS(invoiceId!), updatePayload);
      const data = response.data;

      if (!data.success) {
        console.error('❌ [E-FATURA] SQL güncelleme hatası:', data);
        // Hata olsa bile gönderim başarılı, sadece log'la
        return;
      }

      console.log('✅ [E-FATURA] SQL başarıyla güncellendi:', data);
    } catch (error: any) {
      console.error('❌ [E-FATURA] SQL güncelleme hatası:', error);
      // Hata olsa bile gönderim başarılı, sadece log'la
    }
  };

  const handleSendEInvoice = async () => {
    // ✅ Çift gönderim koruması: istek uçarken ikinci tıklama yok sayılır
    if (sendingLockRef.current) return;
    // ✅ İade faturasında orijinal fatura referansı olmadan göndermeyi engelle
    if (isReturnType && !selectedReturnInvoiceId) {
      setSendError('İade faturası için iade edilen orijinal faturayı seçmelisiniz.');
      setCurrentStep(0);
      return;
    }
    sendingLockRef.current = true;
    setIsSending(true);
    setSendError(null);
    setSendResult(null);

    try {
      console.log('📤 [E-FATURA] Fatura gönderiliyor...');
      
      // ✅ Entegrasyon kontrolü
      if (!eInvoiceIntegration) {
        setSendError('E-Fatura entegrasyonu bulunamadı. Lütfen önce entegrasyon ayarlarını yapın.');
        setIsSending(false); sendingLockRef.current = false;
        return;
      }

      // ✅ EDM/PavoPay: gts_einvoice_api üzerinden gönder. Backend UBL üretir, mükellef
      // kontrolüyle e-fatura/e-arşiv belirler, gönderir ve fatura durumunu SQL'e yazar.
      // Controller provider-agnostiktir: entegrasyonun ProviderType'ına göre doğru
      // entegratöre (EdmProvider/PavoProvider) yönlenir.
      // Kimlik OwnJwt claim'inden; credential SQL'den. Frontend yalnızca invoiceId yollar.
      if (['edm', 'pavopay'].includes(eInvoiceIntegration.providerType)) {
        try {
          // Kullanıcı bu gönderime özel seri seçtiyse ekle (yalnızca EDM'de anlamlı)
          const serialPayload = eInvoiceIntegration.providerType === 'edm' && selectedSerial
            ? { serialPrefix: selectedSerial }
            : {};
          // Tasarım şablonu seçimi ('' = otomatik → gönderilmez; 'none'/'system'/Id → gönderilir)
          const xsltPayload = selectedXsltTemplate ? { xsltTemplateId: selectedXsltTemplate } : {};
          console.log('📤 [E-FATURA] Entegratör gönderimi (einvoice servisi):', { provider: eInvoiceIntegration.providerType, invoiceId, ...returnRefPayload, ...serialPayload, ...xsltPayload });
          const resp = await einvoiceApi.post(EINVOICE_API.EDM_SEND, { invoiceId, ...returnRefPayload, ...serialPayload, ...xsltPayload });
          const result = resp.data;
          console.log('✅ [E-FATURA] EDM yanıtı:', result);
          setSendResult(result);
          if (!result?.success) {
            setSendError(result?.error || result?.message || 'Gönderim başarısız');
          } else if (invoiceId) {
            await refreshB2BMirrorByInvoice(invoiceId);
          }
          // Not: Durum SQL'e backend tarafında yazıldı; ek güncelleme yapmıyoruz.
        } catch (err: any) {
          console.error('❌ [E-FATURA] EDM gönderim hatası:', err);
          const data = err?.response?.data;
          setSendError(data?.error || data?.errorLong || data?.message || handleApiError(err));
          if (data) setSendResult(data);
        } finally {
          setIsSending(false); sendingLockRef.current = false;
        }
        return;
      }

      console.log('📋 [E-FATURA] XML İçeriği uzunluğu:', xmlContent.length);
      console.log('🔐 [E-FATURA] Kullanılan entegrasyon:', {
        providerType: eInvoiceIntegration.providerType,
        username: eInvoiceIntegration.username,
        companyName: eInvoiceIntegration.companyName,
        taxNumber: eInvoiceIntegration.taxNumber,
      });
      
      // ✅ SQL'den gelen entegrasyon bilgileri
      const username = eInvoiceIntegration.username;
      const password = eInvoiceIntegration.password;
      const vkn = eInvoiceIntegration.taxNumber;
      const providerType = eInvoiceIntegration.providerType; // 'finansbank' veya 'edm'
      const belgeTuru = 'FATURA_UBL'; // UBL formatında fatura gönderiyoruz

      console.log('🔑 [E-FATURA] Gönderim Parametreleri:', {
        username,
        vkn,
        belgeTuru,
        providerType,
        xmlLength: xmlContent.length
      });

      // ✅ Provider tipine göre farklı API kullan
      const eInvoiceApiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!eInvoiceApiUrl) {
        setSendError('E-Fatura API URL yapılandırması bulunamadı. Lütfen REACT_APP_EINVOICE_URL ortam değişkenini kontrol edin.');
        setIsSending(false); sendingLockRef.current = false;
        return;
      }

      let apiUrl = '';
      if (providerType === 'finansbank') {
        apiUrl = `${eInvoiceApiUrl}/QNBESolutions/BelgeGonderExt`;
      } else {
        // EDM yukarıda yeni servise yönlendirildi; buraya yalnızca bilinmeyen tip düşer.
        setSendError(`Desteklenmeyen entegrasyon tipi: ${providerType}`);
        setIsSending(false); sendingLockRef.current = false;
        return;
      }

      console.log('🌐 [E-FATURA] API URL:', apiUrl);

      // QNB/Finansbank API'ye belge gönder
      const response = await fetch(
        `${apiUrl}?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&vkn=${vkn}&belgeTuru=${belgeTuru}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(xmlContent), // Backend [FromBody] string bekliyor, JSON string olarak gönderiyoruz
        }
      );

      console.log('📡 [E-FATURA] Response status:', response.status);

      const result = await response.json();

      if (!response.ok) {
        // Hata durumu
        console.error('❌ [E-FATURA] Gönderim hatası:', result);
        
        // Backend hem Error hem error field'ı döndürebilir (case-insensitive)
        const errorMessage = result.Error || result.error || result.Details || result.details || result.message || 'Bilinmeyen hata oluştu';
        const errorDetails = result.Details || result.details || '';
        
        setSendError(errorDetails ? `${errorMessage} - ${errorDetails}` : errorMessage);
        setSendResult(result);
      } else {
        // Başarılı durum
        console.log('✅ [E-FATURA] Gönderim başarılı:', result);
        setSendResult(result);
        
        // ✅ SQL'e E-Fatura durumunu kaydet
        await updateInvoiceEInvoiceStatus(result);
        if (invoiceId) {
          await refreshB2BMirrorByInvoice(invoiceId);
        }
        
        // ✅ Modal KAPANMASIN - Kullanıcı sonucu görsün
        // Kullanıcı "Kapat" butonuna bastığında onSuccess() çağrılacak
      }
    } catch (error: any) {
      console.error('❌ [E-FATURA] İstek hatası:', error);
      setSendError(error.message || 'Bağlantı hatası oluştu');
    } finally {
      setIsSending(false); sendingLockRef.current = false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        // Bilgi Kontrolü (Cari + E-Fatura Detayları + Entegrasyon)
        return (
          <Box>

            {/* Entegrasyon Bilgisi */}
            {loadingIntegration ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2 }}>Entegrasyon bilgileri yükleniyor...</Typography>
              </Box>
            ) : eInvoiceIntegration ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  ✅ Aktif Entegrasyon: {
                    eInvoiceIntegration.providerType === 'finansbank' ? 'Finansbank (QNB)'
                    : eInvoiceIntegration.providerType === 'pavopay' ? 'PavoPay (Aktif Bank)'
                    : 'EDM'
                  }
                </Typography>
                <Typography variant="caption">
                  Kullanıcı: {eInvoiceIntegration.username}{eInvoiceIntegration.taxNumber ? ` | Vergi No: ${eInvoiceIntegration.taxNumber}` : ''}
                </Typography>

                {/* EDM: bu gönderimde kullanılacak fatura serisi seçimi */}
                {eInvoiceIntegration.providerType === 'edm' && (
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Fatura Serisi"
                    value={selectedSerial}
                    onChange={(e) => {
                      setSelectedSerial(e.target.value);
                      setVisualPreviewLoadedFor('');
                    }}
                    sx={{ mt: 1.5, backgroundColor: 'white', borderRadius: 1 }}
                    helperText="Resmi fatura numarası EDM'deki son sıranın bir fazlasından üretilir. Otomatik: entegrasyon ayarındaki seri."
                  >
                    <MenuItem value="">
                      Otomatik{eInvoiceIntegration.eInvoiceSerialPrefix ? ` (${eInvoiceIntegration.eInvoiceSerialPrefix})` : ''}
                    </MenuItem>
                    {/* Kayıtlı seri EDM listesinde yoksa da seçilebilir kalsın (out-of-range uyarısını önler) */}
                    {selectedSerial && !edmSerials.some((s: any) => s.code === selectedSerial) && (
                      <MenuItem value={selectedSerial}>
                        {selectedSerial} — Kayıtlı seri (entegrasyon ayarı)
                      </MenuItem>
                    )}
                    {edmSerials.map((s: any, idx: number) => {
                      const last = Number(s.lastSerialUsed);
                      const nextHint = Number.isFinite(last)
                        ? ` • sonraki ${last + 1}`
                        : s.lastSerialUsed != null && s.lastSerialUsed !== ''
                          ? ` • son ${s.lastSerialUsed}`
                          : ' • ilk no';
                      return (
                        <MenuItem key={`${s.code}-${idx}`} value={s.code}>
                          {s.code} — {s.isArchive ? 'E-Arşiv' : 'E-Fatura'} ({s.year}){nextHint}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                )}

                {/* Tasarım şablonu (XSLT) seçimi — EDM ve Pavo (UBL'e gömülür) */}
                {['edm', 'pavopay'].includes(eInvoiceIntegration.providerType) && (
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Tasarım Şablonu"
                    value={selectedXsltTemplate}
                    onChange={(e) => setSelectedXsltTemplate(e.target.value)}
                    sx={{ mt: 1.5, backgroundColor: 'white', borderRadius: 1 }}
                    helperText="Faturanın görsel tasarımı. Otomatik: varsayılan işaretli şablon; şablon yoksa entegratörün tasarımı kullanılır."
                  >
                    <MenuItem value="">
                      Otomatik{xsltTemplates.some((t: any) => t.isDefault) ? ` (${xsltTemplates.find((t: any) => t.isDefault)?.name})` : ' (entegratör tasarımı)'}
                    </MenuItem>
                    <MenuItem value="none">Entegratör Tasarımı (şablon gömme)</MenuItem>
                    <MenuItem value="system">Sistem Varsayılanı</MenuItem>
                    {xsltTemplates.map((t: any) => (
                      <MenuItem key={t.id} value={String(t.id)}>
                        {t.name}{t.isDefault ? ' ★' : ''} — {t.documentType === 'EFATURA' ? 'E-Fatura' : t.documentType === 'EARSIV' ? 'E-Arşiv' : 'Hepsi'}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  ⚠️ Aktif entegrasyon bulunamadı
                </Typography>
                <Typography variant="caption">
                  Lütfen önce E-Fatura entegrasyon ayarlarını yapın.
                </Typography>
              </Alert>
            )}

            {/* Cari Bilgileri */}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Cari Bilgileri
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Cari Ünvanı</TableCell>
                    <TableCell>{invoiceData?.accountName || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Vergi Numarası</TableCell>
                    <TableCell>{invoiceData?.accountTaxNumber || 'Eksik'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Vergi Dairesi</TableCell>
                    <TableCell>{invoiceData?.accountTaxOffice || 'Eksik'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Adres</TableCell>
                    <TableCell>{invoiceData?.accountAddress || 'Eksik'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {getMissingAccountFields().length === 0 && getOptionalMissingAccountFields().length > 0 && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>{getOptionalMissingAccountFields().join(', ')}</strong> bilgisi eksik. E-arşiv/bireysel
                  alıcılarda zorunlu değildir; gönderime engel değildir. Kurumsal (e-Fatura) alıcılarda
                  tamamlanması önerilir.
                </Typography>
              </Alert>
            )}

            {getMissingAccountFields().length > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Eksik Cari Bilgileri:
                </Typography>
                <Typography variant="body2">
                  {getMissingAccountFields().join(', ')}
                </Typography>
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  Lütfen faturayı düzenleyip eksik bilgileri tamamlayın.
                </Typography>
              </Alert>
            )}

            {/* E-Fatura Detayları (Sadece Görüntüleme) */}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              E-Fatura Detayları
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Profil Türü</TableCell>
                    <TableCell>{getDisplayProfileType(eInvoiceProfileType)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fatura Tip Kodu</TableCell>
                    <TableCell>{eInvoiceTypeCode}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* ✅ İade faturası: iade edilen orijinal faturayı seçtir (GİB zorunlu) */}
            {isReturnType && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  İade Edilen Fatura
                </Typography>
                <Alert severity={returnRefLocked ? 'info' : 'warning'} sx={{ mb: 1.5 }}>
                  {returnRefLocked
                    ? <>İade edilen orijinal fatura, <strong>fatura oluşturulurken</strong> belirlendi ve burada değiştirilemez. Farklı bir orijinal için iadeyi doğru faturadan oluşturun.</>
                    : <>İade faturalarında, iade edilen <strong>orijinal faturanın</strong> seçilmesi zorunludur. GİB, iade bilgisini içeren referansı (16 haneli fatura no) ister.</>}
                </Alert>
                <TextField
                  select
                  fullWidth
                  size="small"
                  required
                  label="İade edilen orijinal fatura"
                  value={selectedReturnInvoiceId}
                  onChange={(e) => setSelectedReturnInvoiceId(e.target.value)}
                  disabled={loadingReturnCandidates || returnRefLocked}
                  helperText={
                    returnRefLocked
                      ? 'Orijinal fatura oluşturmada sabitlendi (değiştirilemez).'
                      : loadingReturnCandidates
                        ? 'Carinin faturaları yükleniyor...'
                        : returnCandidates.length === 0
                          ? 'Bu cariye ait başka fatura bulunamadı.'
                          : 'Seçilen carinin faturalarından iade edilecek olanı seçin.'
                  }
                >
                  {returnCandidates.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.invoiceNumber} · {c.invoiceDate ? new Date(c.invoiceDate).toLocaleDateString('tr-TR') : '-'}
                      {typeof c.grandTotal === 'number' ? ` · ${fmtMoney(c.grandTotal, c.currency)}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
          </Box>
        );

      case 1: {
        // Önizleme — kullanıcı dostu fatura özeti (ham XML teknik detayda)
        const items: any[] = invoiceData?.items || [];
        const cur = invoiceData?.currency || 'TRY';
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Fatura aşağıdaki bilgilerle gönderilecek. Lütfen kontrol edip "İleri" ile devam edin.
            </Alert>

            {/* Üst bilgi: Fatura no / tarih / profil */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack direction="row" flexWrap="wrap" gap={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Fatura No</Typography>
                  <Typography variant="body1" fontWeight="bold">{invoiceData?.invoiceNumber || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Tarih</Typography>
                  <Typography variant="body1">
                    {invoiceData?.invoiceDate ? new Date(invoiceData.invoiceDate).toLocaleDateString('tr-TR') : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Profil</Typography>
                  <Typography variant="body1">{getDisplayProfileType(eInvoiceProfileType)} · {eInvoiceTypeCode}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Belge Tipi</Typography>
                  <Box>
                    <Chip size="small" color="default" label="Alıcının mükellefiyetine göre belirlenir (E-Fatura / E-Arşiv)" />
                  </Box>
                </Box>
              </Stack>
            </Paper>

            {/* Taraflar */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Satıcı</Typography>
                <Typography variant="body2">{invoiceData?.sellerCompanyName || invoiceData?.companyName || companyInfo?.companyName || '-'}</Typography>
                <Typography variant="caption" color="text.secondary">VKN: {invoiceData?.sellerTaxNumber || eInvoiceIntegration?.taxNumber || '-'}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Alıcı</Typography>
                <Typography variant="body2">{invoiceData?.accountName || '-'}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {(invoiceData?.accountTaxNumber?.length === 11 ? 'TCKN' : 'VKN')}: {invoiceData?.accountTaxNumber || '-'}
                  {invoiceData?.accountTaxOffice ? ` · ${invoiceData.accountTaxOffice}` : ''}
                </Typography>
                {invoiceData?.accountAddress && (
                  <Typography variant="caption" color="text.secondary">{invoiceData.accountAddress}</Typography>
                )}
              </Paper>
            </Stack>

            {/* Kalemler */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Kalemler ({items.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Ürün / Hizmet</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Miktar</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Birim Fiyat</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">KDV %</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Tutar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center">Kalem bulunamadı</TableCell></TableRow>
                  ) : items.map((it: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{it.itemName || '-'}</TableCell>
                      <TableCell align="right">{Number(it.quantity) || 0} {it.unit || ''}</TableCell>
                      <TableCell align="right">{fmtMoney(it.unitPrice, cur)}</TableCell>
                      <TableCell align="right">{Number(it.vatRate) || 0}</TableCell>
                      <TableCell align="right">{fmtMoney(it.netTotal ?? it.lineTotal, cur)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Toplamlar */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, maxWidth: 360, ml: 'auto' }}>
              <Stack spacing={0.5}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Ara Toplam</Typography>
                  <Typography variant="body2">{fmtMoney(invoiceData?.subtotal, cur)}</Typography>
                </Box>
                {Number(invoiceData?.discountTotal) > 0 && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">İndirim</Typography>
                    <Typography variant="body2">-{fmtMoney(invoiceData?.discountTotal, cur)}</Typography>
                  </Box>
                )}
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">KDV</Typography>
                  <Typography variant="body2">{fmtMoney(invoiceData?.vatTotal, cur)}</Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle2" fontWeight="bold">Genel Toplam</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">{fmtMoney(invoiceData?.grandTotal, cur)}</Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Fatura görseli: seçili tasarım şablonuyla render edilmiş hali (EDM/Pavo) */}
            {['edm', 'pavopay'].includes(eInvoiceIntegration?.providerType) && (
              <Accordion
                variant="outlined"
                disableGutters
                sx={{ mb: 1, '&:before': { display: 'none' } }}
                onChange={(_, expanded) => { if (expanded) loadVisualPreview(); }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <VisibilityIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Fatura Görseli (tasarım şablonuyla)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {visualPreviewLoading && (
                    <Box display="flex" alignItems="center" gap={1.5} py={3} justifyContent="center">
                      <CircularProgress size={20} />
                      <Typography variant="body2">Görsel önizleme oluşturuluyor...</Typography>
                    </Box>
                  )}
                  {!visualPreviewLoading && visualPreviewError && (
                    <Alert severity="info">
                      {visualPreviewError}
                      {visualPreviewError.includes('şablon bulunamadı') && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          Tasarım şablonu tanımlı değil; fatura entegratörün hesap tasarımıyla oluşturulur.
                          Kendi tasarımınız için E-Fatura ekranındaki ⋮ menüsünden "Tasarım Şablonları"nı kullanın.
                        </Typography>
                      )}
                    </Alert>
                  )}
                  {!visualPreviewLoading && !visualPreviewError && visualPreviewHtml && (
                    <Box
                      component="iframe"
                      srcDoc={visualPreviewHtml}
                      title="Fatura görsel önizleme"
                      // allow-scripts: şablona gömülü karekod üretici çalışsın (allow-same-origin
                      // verilmediği için iframe izole kalır, üst pencereye erişemez).
                      sandbox="allow-scripts"
                      sx={{
                        width: '100%',
                        height: '60vh',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'white',
                      }}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Teknik detay: ham UBL XML (katlanabilir) */}
            <Accordion variant="outlined" disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <CodeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">Teknik detay (UBL XML)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    maxHeight: 360,
                    overflow: 'auto',
                    backgroundColor: '#f5f5f5',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {xmlContent || 'XML önizleme yüklenemedi.'}
                </Paper>
              </AccordionDetails>
            </Accordion>
          </Box>
        );
      }

      case 2:
        // Gönderim
        return (
          <Box>
            {!sendResult && !sendError && !isSending && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  E-fatura GİB'e gönderilmeye hazır.
                </Alert>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Fatura No:</strong> {invoiceData?.invoiceNumber}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Profil Türü:</strong> {getDisplayProfileType(eInvoiceProfileType)}
                </Typography>
                <Typography variant="body1">
                  <strong>Tip Kodu:</strong> {eInvoiceTypeCode}
                </Typography>
              </>
            )}

            {isSending && (
              <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="body1">E-fatura gönderiliyor...</Typography>
              </Box>
            )}

            {sendError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  ❌ Gönderim Hatası
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {sendError}
                </Typography>
                {sendResult && (
                  <Paper
                    variant="outlined"
                    sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: '#fff3f3',
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {JSON.stringify(sendResult, null, 2)}
                    </Typography>
                  </Paper>
                )}
              </Alert>
            )}

            {sendResult && !sendError && (
              <Alert severity={sendResult.alreadyExists ? 'info' : 'success'} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '1.1rem', mb: 1 }}>
                  {sendResult.alreadyExists ? 'ℹ️ Fatura Zaten Gönderilmiş' : '✅ E-Fatura Başarıyla Gönderildi!'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {sendResult.alreadyExists
                    ? (sendResult.message || 'Bu fatura GİB sisteminde zaten mevcut; tekrar gönderilmedi.')
                    : 'Fatura GİB sistemine başarıyla iletildi.'}
                </Typography>
                
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#f0f9ff',
                  }}
                >
                  {/* EDM (yeni servis) özeti */}
                  {sendResult.documentType && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color="success.dark">
                        Belge Türü: {sendResult.documentType === 'EFATURA' ? 'E-Fatura' : 'E-Arşiv'}
                      </Typography>
                      {sendResult.officialNumber && (
                        <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                          Fatura No: {sendResult.officialNumber}
                        </Typography>
                      )}
                      {sendResult.uuid && (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                          UUID: {sendResult.uuid}
                        </Typography>
                      )}
                      {sendResult.returnCode && (
                        <Typography variant="body2">Dönüş Kodu: {sendResult.returnCode}</Typography>
                      )}
                    </Box>
                  )}

                  {/* Belge OID */}
                  {sendResult.belgeGonderMsgOid && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        📝 Belge Gönderim ID (OID)
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                        {sendResult.belgeGonderMsgOid}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Belge Durumu - Detaylı */}
                  {sendResult.belgeDurum?.data && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'success.dark' }}>
                        📊 Belge Durumu:
                      </Typography>
                      
                      <Stack spacing={1.5}>
                        {/* Durum Kodu ve Açıklama */}
                        <Box sx={{ p: 1.5, bgcolor: sendResult.belgeDurum.data.durumKodu === 1 ? '#fff8e1' : '#e8f5e9', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Durum Kodu: {sendResult.belgeDurum.data.durumKodu}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                            {sendResult.belgeDurum.data.durumKoduAciklama || 'Açıklama yok'}
                          </Typography>
                        </Box>
                        
                        {/* Alım Tarihi */}
                        {sendResult.belgeDurum.data.alimTarihi && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              📅 Alım Tarihi:
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {sendResult.belgeDurum.data.alimTarihi}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* ETTN (Varsa) */}
                        {sendResult.belgeDurum.data.ettn && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#e1f5fe', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              🔗 ETTN:
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {sendResult.belgeDurum.data.ettn}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Gönderim Tarihi (Varsa) */}
                        {sendResult.belgeDurum.data.gonderimTarihi && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              📤 Gönderim Tarihi:
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {sendResult.belgeDurum.data.gonderimTarihi}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Durum Açıklaması (Varsa) */}
                        {sendResult.belgeDurum.data.durumAciklama && (
                          <Box sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              💬 Açıklama:
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {sendResult.belgeDurum.data.durumAciklama}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  )}
                  
                  {/* Tam JSON Yanıt (varsayılan KAPALI — istenirse açılır) */}
                  <Accordion disableGutters elevation={0} sx={{ mt: 2, '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 0 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        🔍 Teknik detaylar (tam JSON yanıt)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0 }}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          backgroundColor: '#fafafa',
                          maxHeight: 200,
                          overflow: 'auto',
                          border: '1px dashed #ccc',
                        }}
                      >
                        <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#424242' }}>
                          {JSON.stringify(sendResult, null, 2)}
                        </Typography>
                      </Paper>
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        E-Fatura Gönder
        <GuideButton guideKey="documents.eInvoiceSend" size="small" />
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={currentStep} sx={{ mb: 3, mt: 1 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          renderStepContent()
        )}
      </DialogContent>
      <DialogActions>
        {/* Başarılı gönderim sonrası SADECE "Kapat" butonu göster */}
        {sendResult && !sendError ? (
          <Button 
            onClick={() => {
              onSuccess(); // Listeyi yenile
              onClose(); // Modalı kapat
            }}
            variant="contained"
            color="primary"
            fullWidth
          >
            Kapat ve Listeyi Yenile
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>İptal</Button>
            {currentStep > 0 && !isSending && (
              <Button onClick={handleBack}>Geri</Button>
            )}
            {currentStep < steps.length - 1 ? (
              <Button
                onClick={handleNext}
                variant="contained"
                disabled={
                  (currentStep === 0 && getMissingAccountFields().length > 0) ||
                  (currentStep === 0 && !eInvoiceIntegration) ||
                  loadingIntegration
                }
              >
                İleri
              </Button>
            ) : (
              <Button
                onClick={handleSendEInvoice}
                variant="contained"
                color="success"
                disabled={isSending || !eInvoiceIntegration}
              >
                {isSending ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EInvoiceSendModal;

